import _ from 'lodash';
import React, {useState, useEffect, useCallback} from 'react';
import REST from 'go/requests/rest';
import {connectWithCore} from 'framework/hocs';
import {FemaGoTable} from 'app-ui-componentlibrary';
import {Card, Loader} from 'semantic-ui-react';
import {
  convertValueToDollar,
  formatDollarDecimals,
  displayObjectErrors,
  evaluateTemplateString,
  parseError,
} from 'common/utils';
import {Alert} from 'componentLibrary';
import {Link} from 'react-router-dom';
import {AddCommitmentContainer} from 'componentLibrary/modules/AddCommitmentContainer';
import {FundingInformationCommitmentTable} from 'componentLibrary/modules/FundingInformationCommitmentTable';
import {ALWAYS_DIRTY_MAP} from 'app-ui-componentlibrary/dist/common/constants';
import {MODES, COMMITMENT_STATE} from 'common/requestDetails/constants';

//actions
export function addCommitmentsUtil(entityData, entityIndex, commitmentsToAdd) {
  let updatedEntity = [...entityData];
  let updatedSubEntity = updatedEntity.splice(entityIndex, 1)[0];
  let updatedCommitments = _.get(updatedSubEntity, 'payload.commitment', []);
  updatedCommitments = [...updatedCommitments, ...commitmentsToAdd];
  let updatedPayload = {...updatedSubEntity.payload, commitment: [...updatedCommitments]};
  updatedSubEntity = {...updatedSubEntity, payload: updatedPayload};
  updatedEntity.splice(entityIndex, 0, updatedSubEntity);
  return {
    updatedEntity,
    updatedCommitments,
    entityIndex,
  };
}

export function removeCommitmentUtil(
  entityData,
  entityIndex,
  commitmentId,
  commitmentLineNumber
) {
  let updatedEntity = [...entityData];
  let updatedSubEntity = updatedEntity.splice(entityIndex, 1)[0];
  let updatedCommitments = _.get(updatedSubEntity, 'payload.commitment', []);
  updatedCommitments = _.filter(updatedCommitments, (i) => {
    return (
      i.commitmentNumberId !== commitmentId ||
      i.commitmentLineNumber !== commitmentLineNumber
    );
  });
  let updatedPayload = {...updatedSubEntity.payload, commitment: [...updatedCommitments]};
  updatedSubEntity = {...updatedSubEntity, payload: updatedPayload};
  updatedEntity.splice(entityIndex, 0, updatedSubEntity);
  return {
    updatedEntity,
    updatedCommitments,
    entityIndex,
  };
}

export function updateCommitmentUtil(
  entityData,
  entityIndex,
  commitmentIndex,
  newAmountAwarded,
  availableAmount,
  priorTotalAmount
) {
  let updatedEntity = [...entityData];
  let updatedSubEntity = updatedEntity.splice(entityIndex, 1)[0];
  let updatedCommitments = [..._.get(updatedSubEntity, 'payload.commitment', [])];
  let commitmentToUpdate = updatedCommitments.splice(commitmentIndex, 1)[0];
  const exceedsAvailableAmount = newAmountAwarded
    ? parseFloat(newAmountAwarded) > parseFloat(availableAmount)
    : false;
  commitmentToUpdate = {
    ...commitmentToUpdate,
    awardAmount: newAmountAwarded,
    exceedsAvailableAmount: exceedsAvailableAmount,
    commitmentAvailableAmount: availableAmount,
    priorTotalAmount: priorTotalAmount,
  };
  updatedCommitments.splice(commitmentIndex, 0, commitmentToUpdate);
  let updatedPayload = {...updatedSubEntity.payload, commitment: [...updatedCommitments]};
  updatedSubEntity = {...updatedSubEntity, payload: updatedPayload};
  updatedEntity.splice(entityIndex, 0, updatedSubEntity);
  return {
    updatedEntity,
    updatedCommitments,
    entityIndex,
  };
}

/***
 * Neccessary Config Props
 * - cardSummary
 * - entityDataPath
 * - cardHeaders
 *
 * Necessary Congig Dependencies
 *
 */

const REGIONS = ['HQ', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
export function FundingInformationSectionV2(props) {
  //for prep (or anything with hasSingleEntity tag) this will be an array with awardRec in it, for bric this is a list of subapp data
  const [entityData, setEntityData] = useState([]);
  const [mappedEntities, setMappedEntities] = useState({});
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isEditable, setIsEditable] = useState(true);
  const [init, setInit] = useState(null);

  const {
    cardHeaders,
    cardSummary,
    commitmentHeader,
    commitmentSummary,
    editable,
    hasSingleEntity,
    deleteModalContent,
    newCommitmentFeature,
    displayRegionAlert,
    regionAlertHeader,
    regionAlertContent,
    organization,
    workflow,
    program,
    mode,
    disableMultiCommitmentButton,
    isSubgrantScope,
  } = props;
  const foCommitmentData = _.get(
    props,
    'context.fundingOpportunityData.fundingOpportunityCommitment'
  );

  const grantCommitmentData = isSubgrantScope
    ? _.get(props, 'context.subGrant.payload.commitment')
    : _.get(props, 'context.grant.payload.commitment');

  const amountAwarded = () => {
    if (mode === MODES.CLOSEOUT_RECONCILIATION) {
      return _.get(props, 'context.workflowTask.processVariables.cashOnHand');
    } else if (mode === MODES.CLOSEOUT_RECON_DEOB) {
      return _.get(props, 'context.workflowTask.processVariables.obligationAmount');
    } else {
      return null;
    }
  };

  const isSingleCommitmentCloseout = (grantCommitmentData) => {
    if (mode === MODES.CLOSEOUT_RECONCILIATION || mode === MODES.CLOSEOUT_RECON_DEOB) {
      if (_.isArray(grantCommitmentData) && _.size(grantCommitmentData) > 1) {
        return false;
      } else {
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    if (props.data) {
      const data = props.data instanceof Array ? props.data : [props.data];
      setEntityData(data);
      setInit(true);
    }
    //defaults to true unless specified false in config
    setIsEditable(editable == false ? editable : true);
  }, [props]);

  const fetchData = useCallback(async () => {
    if (entityData.length > 0) {
      let mappedData = await Promise.all(
        _.map(entityData, async (subEntity) => {
          const subEntityCommitments = _.get(subEntity, 'payload.commitment', []);
          const org = _.get(props, 'organization');
          const subOrg = _.find(_.get(props, 'subOrganizations', []), (o) => {
            return o.organizationId == subEntity.organizationId;
          });
          const entityId = _.get(subEntity, 'subGrantId', _.get(subEntity, 'grantId'));
          let mappedEntity = {};
          let commitmentInfo = [];
          if (isSingleCommitmentCloseout(grantCommitmentData)) {
            commitmentInfo = await getCommitments(grantCommitmentData);
            let currentCommitmentInfo = await getCommitments(subEntityCommitments);
            if (_.size(currentCommitmentInfo) < 1) {
              addCommitments(0, commitmentInfo);
            }
          } else {
            commitmentInfo = await getCommitments(subEntityCommitments);
          }
          mappedEntity = {
            entity: {...subEntity, mappedCommitments: commitmentInfo, entityId: entityId},
          };
          mappedEntity = subOrg
            ? {...mappedEntity, subOrganization: {...subOrg}}
            : {...mappedEntity, organization: {...org}};

          mappedEntity.program = program;
          mappedEntity.workflow = workflow;
          return mappedEntity;
        })
      );
      setMappedEntities(mappedData);
      setDataLoaded(true);
    }
  }, [entityData]);

  useEffect(() => {
    if (isSingleCommitmentCloseout(grantCommitmentData)) {
      setIsEditable(false);
    }
    if (entityData.length > 0 && init) {
      fetchData();
    }
  }, [entityData, fetchData, init]);

  const formatLineNumber = (lineNumber) => {
    if (lineNumber === undefined) return lineNumber;
    while (`${lineNumber}`.length < 3) lineNumber = '0' + `${lineNumber}`;
    return lineNumber;
  };

  function getEntityErrors(entityIndex) {
    const {validationErrors, entityType} = props;
    let fieldname = entityType;
    // need to check after validation is implemented
    let objPath = hasSingleEntity
      ? `0.${fieldname}.${entityIndex}`
      : `0.${fieldname}.0.error.${entityIndex}`;
    let errors = _.get(validationErrors, objPath);
    return errors;
  }

  function getSingleCommitmentErrors(entityIndex) {
    let fieldname = 'singleCommitment';
    let entityErrors = getEntityErrors(entityIndex);
    let errors = _.get(entityErrors, `${fieldname}.0`, {isValid: true});
    return errors;
  }

  function getCommitmentErrors(entityIndex, commitmentIndex) {
    let fieldname = 'commitment';
    let entityErrors = getEntityErrors(entityIndex);
    // need to check after validation is implemented
    let errors = _.get(
      parseError(_.get(entityErrors, `${fieldname}`)),
      `error.${commitmentIndex}`,
      {isValid: true}
    );
    return errors;
  }

  function getFedShareErrors(entityIndex) {
    let fieldname = 'fedShare';
    let entityErrors = getEntityErrors(entityIndex);
    let errors = parseError(_.get(entityErrors, `${fieldname}`)) || {isValid: true};
    return errors;
  }

  function getCashOnHandErrors(entityIndex) {
    let fieldname = 'cashOnHand';
    let entityErrors = getEntityErrors(entityIndex);
    let errors = _.get(entityErrors, `${fieldname}.0`, {isValid: true});
    return errors;
  }

  function displayCommitmentError(entityIndex, commitmentIndex, fieldname) {
    let commitmentErrors = getCommitmentErrors(entityIndex, commitmentIndex);
    let result = displayObjectErrors(commitmentErrors, ALWAYS_DIRTY_MAP, fieldname);
    return result;
  }

  const formatCommitmentsForSave = (commitments) => {
    let commitmentsForSave = [];
    _.forEach(commitments, (commitmentToFormat) => {
      let newCommitment = {
        accsline: commitmentToFormat.accsline,
        commitmentLineNumber: commitmentToFormat.commitmentLineNumber,
        commitmentNumberId: commitmentToFormat.commitmentNumberId,
        region: commitmentToFormat.region,
        awardAmount: commitmentToFormat.awardAmount,
        exceedsAvailableAmount: commitmentToFormat.exceedsAvailableAmount,
        commitmentAvailableAmount: commitmentToFormat.commitmentAvailableAmount,
        priorTotalAmount: commitmentToFormat.priorTotalAmount,
      };
      if (!_.isNil(commitmentToFormat.state)) {
        newCommitment.state = commitmentToFormat.state;
      }
      commitmentsForSave.push(newCommitment);
    });
    return commitmentsForSave;
  };

  //actions
  const addCommitments = (entityIndex, commitmentsToAdd) => {
    const {newCommitmentFeature} = props;
    if (newCommitmentFeature) {
      _.forEach(commitmentsToAdd, (commitment) => {
        commitment.state = COMMITMENT_STATE.NEW;
      });
    }
    const data = addCommitmentsUtil(entityData, entityIndex, commitmentsToAdd);
    saveData(data);
  };

  const removeCommitment = (entityIndex, commitmentId, commitmentLineNumber) => {
    const data = removeCommitmentUtil(
      entityData,
      entityIndex,
      commitmentId,
      commitmentLineNumber
    );
    saveData(data);
  };

  const updateCommitment = (entityIndex, commitmentIndex, newAmountAwarded) => {
    let availableAmount = _.get(
      mappedEntities,
      `${entityIndex}].entity.mappedCommitments.${commitmentIndex}.commitmentAvailableAmount`
    );
    let priorTotalAmount = _.get(
      mappedEntities,
      `${entityIndex}].entity.mappedCommitments.${commitmentIndex}.priorTotalAmount`
    );
    const data = updateCommitmentUtil(
      entityData,
      entityIndex,
      commitmentIndex,
      newAmountAwarded,
      availableAmount,
      priorTotalAmount
    );
    saveData(data);
  };

  const saveData = ({updatedEntity, updatedCommitments, entityIndex}) => {
    saveEntity(formatCommitmentsForSave(updatedCommitments), entityIndex);
    setEntityData(updatedEntity);
    setDataLoaded(false);
  };

  const saveEntity = (updatedCommitments, entityIndex) => {
    let {contextPathToSave, entityType, saveEntityData} = props;
    if (hasSingleEntity) {
      saveEntityData(
        `${contextPathToSave}.payload.commitment`,
        entityType,
        updatedCommitments,
        null,
        null,
        `${contextPathToSave}`,
        true
      );
    } else {
      saveEntityData(
        `${contextPathToSave}[${entityIndex}].payload.commitment`,
        entityType,
        updatedCommitments,
        null,
        null,
        `${contextPathToSave}.${entityIndex}`,
        true
      );
    }
  };

  const getCommitments = async (commitmentsToRetrieve) => {
    //unique even in amount awarded?
    let commitmentsToRetrieveUniq = [];
    if (_.isArray(commitmentsToRetrieve)) {
      commitmentsToRetrieveUniq = _.uniq(commitmentsToRetrieve);
    } else {
      let temp = [];
      temp.push(commitmentsToRetrieve);
      commitmentsToRetrieveUniq = _.uniq(temp);
    }
    let formattedCommitments = [];

    try {
      const commitments = await Promise.all(
        _.map(commitmentsToRetrieveUniq, async (commitment) => {
          let commitmentNumber = _.get(
            commitment,
            'commitmentNumberId',
            _.get(commitment, 'commitmentNumber')
          );

          const newData = await REST.commitment
            .getCommitment(commitmentNumber)
            .then(REST.handler.handleResponse)
            .catch((err) => {});
          return {commitment, newData};
        })
      );

      _.forEach(commitments, async ({commitment, newData}) => {
        _.forEach(newData.commitmentDetailsResponse, (commitmentData) => {
          let lineNumber = _.get(commitment, 'commitmentLineNumber');
          const commitmentLineNumber = _.get(commitmentData, 'commitmentLineNumber');
          let commitmentNumber = _.get(
            commitment,
            'commitmentNumberId',
            _.get(commitment, 'commitmentNumber')
          );
          //if there is a previously saved commitment (with line number) then we should only format that one, otherwise we can format all of them
          if (
            commitmentLineNumber &&
            ((lineNumber &&
              formatLineNumber(commitmentLineNumber) === formatLineNumber(lineNumber)) ||
              !lineNumber) &&
            !_.find(formattedCommitments, (o) => {
              return (
                o.commitmentNumberId == commitmentNumber &&
                formatLineNumber(o.commitmentLineNumber) ==
                  formatLineNumber(commitmentLineNumber)
              );
            })
          ) {
            let awardAmount = isSingleCommitmentCloseout(grantCommitmentData)
              ? _.round(_.subtract(0, amountAwarded()), 2).toString()
              : _.get(commitment, 'awardAmount')
              ? formatDollarDecimals(`${_.get(commitment, 'awardAmount')}`)
              : undefined;
            let isGrantArray = _.isArray(grantCommitmentData);
            let commitmentIndex = _.findIndex(grantCommitmentData, [
              'commitmentNumberId',
              commitmentData.commitmentNumberId,
            ]);
            let priorTotalAmount = isGrantArray
              ? _.toNumber(
                  _.get(grantCommitmentData[commitmentIndex], 'awardAmount', '0')
                )
              : _.round(_.subtract(0, amountAwarded()), 2).toString();
            let commitmentAvailableAmount = commitmentData.commitmentAvailableAmount;
            let exceedsAvailableAmount = awardAmount
              ? parseFloat(awardAmount) >= parseFloat(commitmentAvailableAmount)
              : false;
            let formattedCommitment = {
              accsline: commitmentData.accsline,
              priorTotalAmount: priorTotalAmount,
              commitmentAvailableAmount: commitmentAvailableAmount,
              commitmentLineNumber: formatLineNumber(commitmentData.commitmentLineNumber),
              commitmentNumberId: commitmentData.commitmentNumberId,
              commitmentEndDate: commitmentData.commitmentEndDate,
              region: _.get(
                commitment,
                'region',
                REGIONS[_.get(commitment, 'commitmentRegion', 0)]
              ),
              exceedsAvailableAmount: exceedsAvailableAmount,
              awardAmount: awardAmount,
            };
            if (!_.isNil(_.get(commitment, 'state'))) {
              formattedCommitment.state = commitment.state;
            }
            formattedCommitments.push(formattedCommitment);
          }
        });
      });
      return formattedCommitments;
    } catch (e) {
      throw e;
    }
  };

  const fundingInformationAlerts = (entityIndex) => {
    let fedShareError = getFedShareErrors(entityIndex);
    let singleCommitmentError = getSingleCommitmentErrors(entityIndex);
    let cashOnHandError = getCashOnHandErrors(entityIndex);

    return (
      <>
        {!fedShareError.isValid && (
          <React.Fragment>
            <Alert
              type="error"
              header={_.get(fedShareError, 'message')}
              key={`fedShareError-${entityIndex}`}
            />
          </React.Fragment>
        )}
        {!cashOnHandError.isValid && (
          <React.Fragment>
            <Alert
              type="error"
              header={_.get(cashOnHandError, 'message')}
              key={`cashOnHandError-${entityIndex}`}
            />
          </React.Fragment>
        )}
        {!singleCommitmentError.isValid && (
          <React.Fragment>
            <Alert
              type="error"
              header={_.get(singleCommitmentError, 'header')}
              content={_.get(singleCommitmentError, 'message')}
              key={`singletCommitmentError-${entityIndex}`}
            />
          </React.Fragment>
        )}
      </>
    );
  };

  return (
    <React.Fragment>
      {mappedEntities.length > 0 &&
        _.map(mappedEntities, (entity, index) => {
          let entityId = _.get(entity, 'entity.entityId', '');
          return (
            <Card fluid key={entityId}>
              <Card.Content>
                {fundingInformationAlerts(index)}
                <FemaGoTable type="static" summary={cardSummary} basic="very">
                  <FemaGoTable.Header>
                    <FemaGoTable.Row>
                      {_.map(cardHeaders, (header, index) => (
                        <FemaGoTable.HeaderCell key={index}>
                          {header.header}
                        </FemaGoTable.HeaderCell>
                      ))}
                    </FemaGoTable.Row>
                  </FemaGoTable.Header>
                  <FemaGoTable.Body>
                    <FemaGoTable.Row>
                      {_.map(cardHeaders, (header, index) => {
                        let path = header.path;
                        let value;
                        if (header.value) {
                          value = header.value;
                        } else if (header.template) {
                          let obj = {};
                          const keys = Object.keys(header.dataPaths);
                          _.each(
                            keys,
                            (key) => (obj[key] = _.get(entity, header.dataPaths[key], 0))
                          );
                          let tempValue = evaluateTemplateString(header.template, {
                            ...obj,
                          });
                          value =
                            header.type === 'dollar'
                              ? convertValueToDollar(tempValue)
                              : tempValue;
                        } else {
                          value =
                            header.type === 'dollar'
                              ? convertValueToDollar(_.get(entity, path, 0))
                              : _.get(entity, path);
                        }
                        if (header.key && header.key === 'subGrantId') {
                          value = (
                            <Link to={`/subgrant/${value}`} target="_blank">
                              {value}
                            </Link>
                          );
                        } else if (header.key && header.key === 'grantId') {
                          value = <Link to={`/grant/${value}`}>{value}</Link>;
                        }
                        return (
                          <FemaGoTable.TextCell key={index}>{value}</FemaGoTable.TextCell>
                        );
                      })}
                    </FemaGoTable.Row>
                  </FemaGoTable.Body>
                </FemaGoTable>
                {!dataLoaded ? (
                  <Loader inline="centered" active>
                    Loading...
                  </Loader>
                ) : (
                  <FundingInformationCommitmentTable
                    updateCommitment={updateCommitment}
                    removeCommitment={removeCommitment}
                    mappedCommitments={_.get(entity, 'entity.mappedCommitments', [])}
                    commitmentHeader={commitmentHeader}
                    commitmentSummary={commitmentSummary}
                    type="edit"
                    editable={isEditable}
                    disableMultiCommitmentButton={disableMultiCommitmentButton}
                    entityId={entityId}
                    entityIndex={index}
                    displayCommitmentError={displayCommitmentError}
                    deleteModalContent={deleteModalContent}
                    newCommitmentFeature={newCommitmentFeature}
                    displayRegionAlert={displayRegionAlert}
                    regionAlertHeader={regionAlertHeader}
                    regionAlertContent={regionAlertContent}
                    organization={organization}
                  />
                )}
                {isEditable && (
                  <AddCommitmentContainer
                    REGIONS={REGIONS}
                    addCommitments={addCommitments}
                    getCommitments={getCommitments}
                    updateCommitment={updateCommitment}
                    removeCommitment={removeCommitment}
                    mappedEntity={entity}
                    foCommitmentData={foCommitmentData}
                    grantCommitmentData={grantCommitmentData}
                    mode={mode}
                    editable={isEditable}
                    disableMultiCommitmentButton={disableMultiCommitmentButton}
                    entityIndex={index}
                    displayRegionAlert={displayRegionAlert}
                    organization={organization}
                    regionAlertHeader={regionAlertHeader}
                    regionAlertContent={regionAlertContent}
                  />
                )}
              </Card.Content>
            </Card>
          );
        })}
    </React.Fragment>
  );
}

export default connectWithCore(FundingInformationSectionV2);
